import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import { ErpApiError, mapErpError } from './erp-api.errors';
import { ERP_API_URL } from './erp-api.tokens';
import {
  WarehouseApiDownload,
  WarehouseDto,
  WarehouseFilterSelects,
  WarehouseFormSelects,
  WarehouseGridDto,
  WarehouseListQuery,
  WarehouseListResult,
  WarehouseRequest,
  WarehouseSelectDto,
  WarehouseToneDto,
} from './warehouses-api.types';

const REQUEST_TIMEOUT_MS = 15_000;
const LIST_PAGE_SIZE = 100;
const WAREHOUSES_PATH = '/store/warehouses';

@Injectable({ providedIn: 'root' })
export class WarehousesApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(ERP_API_URL).replace(/\/+$/, '')}${WAREHOUSES_PATH}`;

  list(query: WarehouseListQuery = {}): Observable<WarehouseListResult> {
    return this.safeList(
      this.http.get<unknown>(this.baseUrl, {
        params: warehouseListParams(query),
      }),
      unwrapList,
    );
  }

  formSelects(): Promise<WarehouseFormSelects> {
    return this.safeGet(`${this.baseUrl}/form-selects`, unwrapFormSelects);
  }

  filterSelects(): Promise<WarehouseFilterSelects> {
    return this.safeGet(`${this.baseUrl}/filter-selects`, unwrapFilterSelects);
  }

  getById(uuid: string): Promise<WarehouseDto> {
    return this.safeGet(`${this.baseUrl}/${uuid}`, unwrapWarehouse);
  }

  create(payload: WarehouseRequest): Promise<WarehouseDto> {
    return this.mutate(
      this.http.post<unknown>(this.baseUrl, payload),
      unwrapWarehouse,
    );
  }

  update(uuid: string, payload: WarehouseRequest): Promise<WarehouseDto> {
    return this.mutate(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      unwrapWarehouse,
    );
  }

  activate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.mutate(
      this.http.delete<unknown>(`${this.baseUrl}/${uuid}`),
      () => undefined,
    );
  }

  exportExcel(query: WarehouseListQuery = {}): Promise<WarehouseApiDownload> {
    return this.download(
      `${this.baseUrl}/excel`,
      warehouseListParams(query, false),
      'almacenes.xlsx',
    );
  }

  private safeList<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Observable<T> {
    return source.pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry({ count: 1, delay: error => retryDelay(error) }),
      map(project),
      catchError(error => throwError(() => mapErpError(error))),
    );
  }

  private safeGet<T>(url: string, project: (body: unknown) => T): Promise<T> {
    return this.request(
      this.http
        .get<unknown>(url)
        .pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      project,
    );
  }

  private mutate<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    return this.request(source, project);
  }

  private async request<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(
        source.pipe(timeout(REQUEST_TIMEOUT_MS)),
      );
      return project(body);
    } catch (error) {
      throw mapErpError(error);
    }
  }

  private async download(
    url: string,
    params: HttpParams,
    fallbackFilename: string,
  ): Promise<WarehouseApiDownload> {
    try {
      const response = await firstValueFrom(
        this.http
          .get(url, {
            params,
            observe: 'response',
            responseType: 'blob',
          })
          .pipe(
            timeout(REQUEST_TIMEOUT_MS),
            retry({ count: 1, delay: error => retryDelay(error) }),
          ),
      );
      return toDownload(response, fallbackFilename);
    } catch (error) {
      throw mapErpError(error);
    }
  }
}

function retryDelay(error: unknown): Observable<number> {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      const seconds =
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
      const jitterMs = Math.floor(Math.random() * 250);
      return timer(seconds * 1000 + jitterMs);
    }
    if (error.status >= 500 || error.status === 0) {
      return timer(300);
    }
  }
  return throwError(() => error);
}

function warehouseListParams(
  query: WarehouseListQuery,
  includePagination = true,
): HttpParams {
  let params = new HttpParams();
  if (includePagination) {
    params = params
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));
  }
  if (query.name) params = params.set('filters[name]', query.name);
  if (query.description) {
    params = params.set('filters[description]', query.description);
  }
  params = setCsv(params, 'select[type]', query.typeUuids);
  params = setCsv(params, 'select[classification]', query.classificationUuids);
  params = setCsv(params, 'select[project]', query.projectUuids);
  params = setCsv(params, 'select[status]', query.statusUuids);
  if (query.sortBy) params = params.set('sortBy', query.sortBy);
  if (query.order) params = params.set('order', query.order);
  return params;
}

function setCsv(
  params: HttpParams,
  key: string,
  values: readonly string[] | undefined,
): HttpParams {
  return values?.length ? params.set(key, values.join(',')) : params;
}

function toDownload(
  response: HttpResponse<Blob>,
  fallbackFilename: string,
): WarehouseApiDownload {
  if (!response.body) throw new ErpApiError('unexpected', 0);
  return {
    blob: response.body,
    filename:
      contentDispositionFilename(response.headers.get('Content-Disposition')) ??
      fallbackFilename,
  };
}

function contentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      return encoded.replace(/^"|"$/g, '');
    }
  }
  return header.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function unwrapList(body: unknown): WarehouseListResult {
  const data = unwrapData(body);
  const page = asRecord(data);
  const nested = page?.['data'];
  if (!Array.isArray(nested)) {
    throw new ErpApiError('unexpected', 0);
  }
  const items = nested.map(item => {
    const warehouse = normalizeGrid(item);
    if (!warehouse) throw new ErpApiError('unexpected', 0);
    return warehouse;
  });
  const pagination = asRecord(page?.['pagination']);
  const totalRecords = pagination?.['totalRecords'];
  return {
    items,
    total: typeof totalRecords === 'number' ? totalRecords : items.length,
  };
}

function unwrapWarehouse(body: unknown): WarehouseDto {
  const warehouse = normalizeDetail(unwrapData(body));
  if (!warehouse) throw new ErpApiError('unexpected', 0);
  return warehouse;
}

function unwrapFormSelects(body: unknown): WarehouseFormSelects {
  const data = asRecord(unwrapData(body));
  if (!data) throw new ErpApiError('unexpected', 0);
  return {
    types: selectList(data['types']),
    classifications: selectList(data['classifications']),
    projects: selectList(data['projects']),
  };
}

function unwrapFilterSelects(body: unknown): WarehouseFilterSelects {
  const data = asRecord(unwrapData(body));
  if (!data) throw new ErpApiError('unexpected', 0);
  return {
    types: selectList(data['types']),
    classifications: selectList(data['classifications']),
    projects: selectList(data['projects']),
    statuses: selectList(data['statuses']),
  };
}

function selectList(value: unknown): WarehouseSelectDto[] {
  if (!Array.isArray(value)) throw new ErpApiError('unexpected', 0);
  return value.map(item => {
    const option = selectDto(item);
    if (!option) throw new ErpApiError('unexpected', 0);
    return option;
  });
}

function normalizeDetail(value: unknown): WarehouseDto | null {
  const grid = normalizeGrid(value);
  const record = asRecord(value);
  const createdAt = record ? stringField(record, 'createdAt') : null;
  const updatedAt = record ? stringField(record, 'updatedAt') : null;
  return grid && createdAt && updatedAt
    ? { ...grid, createdAt, updatedAt }
    : null;
}

function normalizeGrid(value: unknown): WarehouseGridDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const name = stringField(record, 'name');
  const type = selectDto(record['type']);
  const classification = selectDto(record['classification']);
  const status = toneDto(record['status']);
  if (!uuid || !name || !type || !classification || !status) return null;
  const project = nullableSelect(record['project']);
  if (project === undefined) return null;
  const description = nullableString(record['description']);
  if (description === undefined) return null;
  return { uuid, name, type, classification, project, description, status };
}

function nullableSelect(value: unknown): WarehouseSelectDto | null | undefined {
  if (value == null) return null;
  return selectDto(value);
}

function nullableString(value: unknown): string | null | undefined {
  if (value == null) return null;
  return typeof value === 'string' ? value : undefined;
}

function selectDto(value: unknown): WarehouseSelectDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const name = stringField(record, 'name');
  return uuid && name ? { uuid, name } : null;
}

function toneDto(value: unknown): WarehouseToneDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const name = stringField(record, 'name');
  const valueField = stringField(record, 'value');
  const tone = stringField(record, 'tone');
  return name && valueField && tone
    ? { name, value: valueField, tone }
    : null;
}

function stringField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}
